/**
 * Reads the first sheet of an .xlsx file as text cells, together with the
 * pictures placed in it.
 *
 * `read-excel-file` covers plain values, but it skips pictures and can drop
 * empty rows, so a picture could not be matched to its cell. This reads the
 * workbook parts directly instead, keeping every cell at its own position.
 *
 * Pictures come from two places:
 * - "Place in cell" pictures, stored as rich values the cell points at;
 * - floating pictures, matched to the cell their top-left corner is anchored to.
 */
import { readZip, type ZipArchive } from './unzip'

export interface SheetCell {
  /** The cell's value as text; empty for a picture-only cell. */
  text: string
  /** A picture placed in, or anchored to, the cell. */
  image?: File
}

export interface SheetTable {
  /** Column titles from the first non-empty row. */
  headers: string[]
  /** The rows below the titles, each as long as `headers`. Entirely empty rows are left out. */
  rows: SheetCell[][]
}

const IMAGE_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
}

function parseXml(text: string): Document {
  return new DOMParser().parseFromString(text, 'application/xml')
}

/** Elements by local name, whatever namespace prefix the writer used. */
function elements(parent: Document | Element, localName: string): Element[] {
  return Array.from(parent.getElementsByTagNameNS('*', localName))
}

/** The `r:id` / `r:embed` style attribute, whatever its prefix. */
function relAttribute(el: Element, localName: string): string | null {
  for (const attr of Array.from(el.attributes)) {
    if (attr.localName === localName && attr.prefix) return attr.value
  }
  return null
}

function dirOf(path: string): string {
  return path.slice(0, path.lastIndexOf('/') + 1)
}

function resolvePath(baseDir: string, target: string): string {
  if (target.startsWith('/')) return target.slice(1)
  const parts = (baseDir + target).split('/')
  const out: string[] = []
  for (const part of parts) {
    if (part === '..') out.pop()
    else if (part !== '.' && part !== '') out.push(part)
  }
  return out.join('/')
}

/** A part's relationships: id → absolute part path. */
async function readRels(zip: ZipArchive, partPath: string): Promise<Map<string, string>> {
  const dir = dirOf(partPath)
  const text = await zip.readText(`${dir}_rels/${partPath.slice(dir.length)}.rels`)
  const rels = new Map<string, string>()
  if (!text) return rels
  for (const rel of elements(parseXml(text), 'Relationship')) {
    const id = rel.getAttribute('Id')
    const target = rel.getAttribute('Target')
    if (id && target && rel.getAttribute('TargetMode') !== 'External') {
      rels.set(id, resolvePath(dir, target))
    }
  }
  return rels
}

/** "BC12" → { row: 11, col: 54 }, both zero-based. */
function parseCellRef(ref: string): { row: number; col: number } | null {
  const match = /^([A-Z]+)(\d+)$/.exec(ref)
  if (!match) return null
  let col = 0
  for (const ch of match[1]) col = col * 26 + (ch.charCodeAt(0) - 64)
  return { row: Number(match[2]) - 1, col: col - 1 }
}

/** The text of a string item, skipping phonetic hints. */
function stringItemText(item: Element): string {
  return elements(item, 't')
    .filter((t) => t.parentElement?.localName !== 'rPh')
    .map((t) => t.textContent ?? '')
    .join('')
}

/** Whole numbers stay whole and binary noise (0.30000000000000004) is rounded off. */
function numberText(raw: string): string {
  const n = Number(raw)
  return Number.isFinite(n) ? String(Number(n.toPrecision(15))) : raw
}

async function imageFile(zip: ZipArchive, path: string): Promise<File | null> {
  const bytes = await zip.read(path)
  if (!bytes) return null
  const name = path.slice(path.lastIndexOf('/') + 1)
  const type = IMAGE_TYPES[name.slice(name.lastIndexOf('.') + 1).toLowerCase()]
  if (!type) return null
  return new File([new Uint8Array(bytes)], name, { type })
}

/**
 * The media part behind each "Place in cell" picture, by the cell's `vm`
 * (value metadata) index. Follows metadata → rich value → relationship; when
 * the metadata part is missing, Excel's own numbering (vm 1 = rich value 0)
 * is assumed.
 */
async function readCellPictures(zip: ZipArchive): Promise<Map<number, string>> {
  const result = new Map<number, string>()
  const relsXml = await zip.readText('xl/richData/richValueRel.xml')
  const valuesXml = await zip.readText('xl/richData/rdrichvalue.xml')
  if (!relsXml || !valuesXml) return result

  const relTargets = await readRels(zip, 'xl/richData/richValueRel.xml')
  const relPaths = elements(parseXml(relsXml), 'rel').map(
    (rel) => relTargets.get(relAttribute(rel, 'id') ?? '') ?? null
  )

  // Which key of each structure holds the picture's relationship index
  const structuresXml = await zip.readText('xl/richData/rdrichvaluestructure.xml')
  const imageKeyIndex = structuresXml
    ? elements(parseXml(structuresXml), 's').map((s) =>
        elements(s, 'k').findIndex((k) => k.getAttribute('n') === '_rvRel:LocalImageIdentifier')
      )
    : []

  const values = elements(parseXml(valuesXml), 'rv').map((rv) => {
    const keyIndex = imageKeyIndex[Number(rv.getAttribute('s') ?? 0)] ?? 0
    if (keyIndex < 0) return null
    const relIndex = Number(elements(rv, 'v')[keyIndex]?.textContent ?? NaN)
    return Number.isInteger(relIndex) ? (relPaths[relIndex] ?? null) : null
  })

  // vm (1-based) → valueMetadata block → futureMetadata block → rich value index
  const metadataXml = await zip.readText('xl/metadata.xml')
  let valueIndexByVm: (number | null)[] | null = null
  if (metadataXml) {
    const doc = parseXml(metadataXml)
    const future = elements(doc, 'futureMetadata').find(
      (f) => f.getAttribute('name') === 'XLRICHVALUE'
    )
    const futureBlocks = future
      ? elements(future, 'bk').map((bk) => {
          const rvb = elements(bk, 'rvb')[0]
          return rvb ? Number(rvb.getAttribute('i')) : null
        })
      : []
    const valueMetadata = elements(doc, 'valueMetadata')[0]
    if (valueMetadata) {
      valueIndexByVm = elements(valueMetadata, 'bk').map((bk) => {
        const rc = elements(bk, 'rc')[0]
        return rc ? (futureBlocks[Number(rc.getAttribute('v'))] ?? null) : null
      })
    }
  }

  const vmCount = valueIndexByVm?.length ?? values.length
  for (let vm = 1; vm <= vmCount; vm++) {
    const valueIndex = valueIndexByVm ? valueIndexByVm[vm - 1] : vm - 1
    const path = valueIndex !== null ? values[valueIndex] : null
    if (path) result.set(vm, path)
  }
  return result
}

/** Floating pictures on the sheet, keyed "row:col" by their top-left anchor cell. */
async function readAnchoredPictures(
  zip: ZipArchive,
  sheetPath: string,
  sheet: Document
): Promise<Map<string, string>> {
  const result = new Map<string, string>()
  const sheetRels = await readRels(zip, sheetPath)
  for (const drawing of elements(sheet, 'drawing')) {
    const drawingPath = sheetRels.get(relAttribute(drawing, 'id') ?? '')
    const drawingXml = drawingPath ? await zip.readText(drawingPath) : null
    if (!drawingPath || !drawingXml) continue
    const drawingRels = await readRels(zip, drawingPath)
    const doc = parseXml(drawingXml)
    for (const anchor of [...elements(doc, 'twoCellAnchor'), ...elements(doc, 'oneCellAnchor')]) {
      const from = elements(anchor, 'from')[0]
      const blip = elements(anchor, 'blip')[0]
      if (!from || !blip) continue
      const row = Number(elements(from, 'row')[0]?.textContent)
      const col = Number(elements(from, 'col')[0]?.textContent)
      const media = drawingRels.get(relAttribute(blip, 'embed') ?? '')
      const key = `${row}:${col}`
      if (media && Number.isInteger(row) && Number.isInteger(col) && !result.has(key)) {
        result.set(key, media)
      }
    }
  }
  return result
}

/** Reads the first sheet of an .xlsx workbook. Throws when the file is not one. */
export async function readXlsxTable(file: Blob): Promise<SheetTable> {
  const zip = await readZip(file)

  const workbookXml = await zip.readText('xl/workbook.xml')
  if (!workbookXml) throw new Error('Not an Excel workbook')
  const firstSheet = elements(parseXml(workbookXml), 'sheet')[0]
  const workbookRels = await readRels(zip, 'xl/workbook.xml')
  const sheetPath = workbookRels.get(firstSheet ? (relAttribute(firstSheet, 'id') ?? '') : '')
  const sheetXml = sheetPath ? await zip.readText(sheetPath) : null
  if (!sheetPath || !sheetXml) throw new Error('The workbook has no sheets')

  const sharedXml = await zip.readText('xl/sharedStrings.xml')
  const shared = sharedXml ? elements(parseXml(sharedXml), 'si').map(stringItemText) : []

  const sheet = parseXml(sheetXml)
  const cellPictures = await readCellPictures(zip)
  const anchoredPictures = await readAnchoredPictures(zip, sheetPath, sheet)

  // Grid of cells by position; picture parts are loaded once each
  const grid = new Map<number, Map<number, SheetCell>>()
  const images = new Map<string, Promise<File | null>>()
  const loadImage = (path: string) => {
    if (!images.has(path)) images.set(path, imageFile(zip, path))
    return images.get(path)!
  }
  const cellAt = (row: number, col: number): SheetCell => {
    if (!grid.has(row)) grid.set(row, new Map())
    const cells = grid.get(row)!
    if (!cells.has(col)) cells.set(col, { text: '' })
    return cells.get(col)!
  }

  for (const c of elements(sheet, 'c')) {
    const pos = parseCellRef(c.getAttribute('r') ?? '')
    if (!pos) continue
    const type = c.getAttribute('t')
    const raw = elements(c, 'v')[0]?.textContent ?? ''
    const vm = Number(c.getAttribute('vm'))
    const picture = vm ? cellPictures.get(vm) : undefined
    // A picture cell's own value is only an error placeholder (#VALUE!)
    let text = ''
    if (!picture) {
      if (type === 's') text = shared[Number(raw)] ?? ''
      else if (type === 'inlineStr') text = elements(c, 'is').map(stringItemText).join('')
      else if (type === 'b') text = raw === '1' ? 'TRUE' : 'FALSE'
      else if (type === 'str' || type === 'e') text = raw
      else text = raw ? numberText(raw) : ''
    }
    if (!text && !picture) continue
    const cell = cellAt(pos.row, pos.col)
    cell.text = text
    if (picture) {
      const image = await loadImage(picture)
      if (image) cell.image = image
    }
  }
  for (const [key, path] of anchoredPictures) {
    const [row, col] = key.split(':').map(Number)
    const image = await loadImage(path)
    const cell = cellAt(row, col)
    if (image && !cell.image) cell.image = image
  }

  const rowIndexes = [...grid.keys()].sort((a, b) => a - b)
  if (rowIndexes.length === 0) return { headers: [], rows: [] }
  const headerCells = grid.get(rowIndexes[0])!
  const width = Math.max(...headerCells.keys()) + 1
  const headers = Array.from({ length: width }, (_, col) => headerCells.get(col)?.text.trim() ?? '')

  const rows = rowIndexes.slice(1).map((rowIndex) => {
    const cells = grid.get(rowIndex)!
    return Array.from({ length: width }, (_, col) => cells.get(col) ?? { text: '' })
  })
  return { headers, rows: rows.filter((row) => row.some((cell) => cell.text.trim() || cell.image)) }
}
